'use client'

import { Button } from '../button'
import { Col } from '../col'
import { Row } from '../row'
import React, { useState, useCallback } from 'react'
import { Preview } from '../preview'

export interface GalleryProps {
  images: string[]
  renderImage?: (props: {
    src: string
    alt: string
    className?: string
    sizes?: string
    style?: React.CSSProperties
  }) => React.ReactNode
  sameSize?: boolean
  maxCount?: number
}

export const Gallery = ({
  images: images,
  renderImage,
  sameSize = false,
  maxCount = 3,
}: GalleryProps) => {
  const count = images?.length
  const mainImages: string[] =
    count > maxCount ? images.slice(0, maxCount) : images
  const extraCount = count > maxCount ? count - maxCount : 0

  const defaultRenderImage = ({
    src,
    alt,
    className,
    sizes,
    style,
  }: {
    src: string
    alt: string
    className?: string
    sizes?: string
    style?: React.CSSProperties
  }) => (
    <img
      src={src}
      alt={alt}
      className={className}
      style={style}
      sizes={sizes}
    />
  )
  const _renderImage = renderImage || defaultRenderImage

  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewIndex, setPreviewIndex] = useState(0)
  const handleOpenPreview = useCallback((idx: number) => {
    setPreviewIndex(idx)
    setPreviewOpen(true)
  }, [])

  if (count === 0) return null

  return (
    <>
      {count === 1 && !sameSize && (
        <div
          className='relative w-full h-[128px] md:h-[387px] cursor-pointer'
          onClick={() => handleOpenPreview(0)}
        >
          {_renderImage({
            src: images[0] || '',
            alt: 'gallery',
            className: 'object-cover rounded-xs',
            sizes: '(max-width: 768px) 100vw, 900px',
            style: {
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: 'var(--radius-xs)',
            },
          })}
        </div>
      )}
      {count === 2 && !sameSize && (
        <Row className='gap-xs md:gap-md h-[128px] md:h-[387px]'>
          <Col className='w-2/3 h-full'>
            <div
              className='relative w-full h-full cursor-pointer'
              onClick={() => handleOpenPreview(1)}
            >
              {_renderImage({
                src: images[0] || '',
                alt: 'gallery',
                className: 'object-cover rounded-xs',
                sizes: '(max-width: 768px) 67vw, 600px',
                style: {
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: 'var(--radius-xs)',
                },
              })}
            </div>
          </Col>
          <Col className='w-1/3 h-full'>
            <div
              className='relative w-full h-full cursor-pointer'
              onClick={() => handleOpenPreview(0)}
            >
              {_renderImage({
                src: images[1] || '',
                alt: 'gallery',
                className: 'object-cover rounded-xs',
                sizes: '(max-width: 768px) 33vw, 300px',
                style: {
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: 'var(--radius-xs)',
                },
              })}
            </div>
          </Col>
        </Row>
      )}
      {count > 2 && !sameSize && (
        <Row className='gap-xs md:gap-md items-stretch h-[128px] md:h-[387px]'>
          <Col className='w-2/3 h-full'>
            <div
              className='relative w-full h-full cursor-pointer'
              onClick={() => handleOpenPreview(2)}
            >
              {_renderImage({
                src: mainImages[0] || '',
                alt: 'gallery',
                className: 'object-cover rounded-xs',
                sizes: '(max-width: 768px) 67vw, 600px',
                style: {
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: 'var(--radius-xs)',
                },
              })}
            </div>
          </Col>
          <Col className='gap-xs md:gap-md w-1/3 h-full flex flex-col'>
            <div
              className='relative w-full h-1/2 cursor-pointer'
              onClick={() => handleOpenPreview(0)}
            >
              {_renderImage({
                src: mainImages[1] || '',
                alt: 'gallery',
                className: 'object-cover rounded-xs',
                sizes: '(max-width: 768px) 33vw, 300px',
                style: {
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: 'var(--radius-xs)',
                },
              })}
            </div>
            <div
              className='relative w-full h-1/2 cursor-pointer'
              onClick={() => handleOpenPreview(1)}
            >
              {_renderImage({
                src: mainImages[2] || '',
                alt: 'gallery',
                className: 'object-cover rounded-xs',
                sizes: '(max-width: 768px) 33vw, 300px',
                style: {
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: 'var(--radius-xs)',
                },
              })}
              {extraCount > 0 && (
                <Button
                  variant='ghost'
                  color='base'
                  size='xs'
                  className='absolute bottom-4 left-4 px-2 bg-base-surface-light/80'
                >
                  {extraCount}+ تصویر
                </Button>
              )}
            </div>
          </Col>
        </Row>
      )}
      {sameSize && (
        <Row className='gap-xs md:gap-md items-stretch h-[120px] '>
          <Row className='gap-xs md:gap-md  h-full'>
            <div
              className='relative w-full h-full cursor-pointer max-w-[230px]'
              onClick={() => handleOpenPreview(0)}
            >
              {_renderImage({
                src: mainImages[0] || '',
                alt: 'gallery',
                className: 'object-cover rounded-2xs',
                sizes: '(max-width: 768px) 33vw, 200px',
                style: {
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: 'var(--radius-xs)',
                },
              })}
            </div>
            {mainImages[1] && (
              <div
                className='relative w-full h-full cursor-pointer max-w-[230px]'
                onClick={() => handleOpenPreview(1)}
              >
                {_renderImage({
                  src: mainImages[1] || '',
                  alt: 'gallery',
                  className: 'object-cover rounded-2xs',
                  sizes: '(max-width: 768px) 33vw, 200px',
                  style: {
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: 'var(--radius-xs)',
                  },
                })}
                {extraCount > 0 && maxCount === 2 && (
                  <Button
                    variant='ghost'
                    color='base'
                    size='xs'
                    className='absolute bottom-4 left-2 md:left-4 px-2 bg-base-surface-light/80'
                  >
                    {extraCount}+ تصویر
                  </Button>
                )}
              </div>
            )}
            {mainImages[2] && (
              <div
                className='relative w-full h-full cursor-pointer max-w-[230px]'
                onClick={() => handleOpenPreview(2)}
              >
                {_renderImage({
                  src: mainImages[2] || '',
                  alt: 'gallery',
                  className: 'object-cover rounded-2xs',
                  sizes: '(max-width: 768px) 33vw, 200px',
                  style: {
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: 'var(--radius-xs)',
                  },
                })}
                {extraCount > 0 && maxCount === 3 && (
                  <Button
                    variant='ghost'
                    color='base'
                    size='xs'
                    className='absolute bottom-4 left-2 md:left-4 px-2 bg-base-surface-light/80'
                  >
                    {extraCount}+ تصویر
                  </Button>
                )}
              </div>
            )}
            {mainImages[3] && (
              <div
                className='relative w-full h-full cursor-pointer max-w-[230px]'
                onClick={() => handleOpenPreview(3)}
              >
                {_renderImage({
                  src: mainImages[3] || '',
                  alt: 'gallery',
                  className: 'object-cover rounded-2xs',
                  sizes: '(max-width: 768px) 33vw, 200px',
                  style: {
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: 'var(--radius-xs)',
                  },
                })}
                {extraCount > 0 && maxCount === 4 && (
                  <Button
                    variant='ghost'
                    color='base'
                    size='xs'
                    className='absolute bottom-4 left-1 md:left-4 px-2 bg-base-surface-light/80'
                  >
                    {extraCount}+ تصویر
                  </Button>
                )}
              </div>
            )}
          </Row>
        </Row>
      )}
      <Preview
        images={images}
        open={previewOpen}
        initialIndex={previewIndex}
        onClose={() => setPreviewOpen(false)}
      />
    </>
  )
}
